import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Container, Dropdown } from 'semantic-ui-react';

import { kebabCase } from 'lodash';

import '../../css/CourseResourceFilterModal.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import { DIALOG_NAMES } from '../../managers/DialogManager';

const CourseResourceFilterModal = observer((props = {}) => {
  const {
    buttonKeyCancelLabel = 'cancel',
    buttonKeyConfirmLabel = 'filter',
    className = 'CourseResourceFilterModal',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disableKeystroke = false,
    disableMove = false,
    disableResize = true,
    initHeight,
    initWidth,
    minHeight = 410,
    minWidth = 540,
    onClose,
    onOpen,
    onRequestClose,
    open: isOpen,
    overlay = 'medium',
    t,
    top = window.innerHeight / 6
  } = props;

  const {
    courseResourceFilterManager,
    dialogManager
  } = useContext(MobXProviderContext);

  const GenericModal = SatCoreComponent('GenericModal');

  const [selectedTagsMap, setSelectedTagsMap] = useState(new Map());
  const scrollPositionRef = useRef(null);

  const hasCategories = courseResourceFilterManager.availableCourseResourceFilterCategories?.length;

  // Initialize local state from courseResourceFilterManager when modal opens
  useEffect(() => {
    const initialMap = new Map();
    const currentlySelected = courseResourceFilterManager.selectedCourseResourceFilterTagIds || [];

    courseResourceFilterManager.availableCourseResourceFilterCategories.forEach((category) => {
      const categorySelectedTags = category.tags
        .filter((tag) => currentlySelected.includes(tag.id))
        .map((tag) => tag.id);

      if (categorySelectedTags.length > 0) {
        initialMap.set(category.id, categorySelectedTags);
      }
    });
    setSelectedTagsMap(initialMap);
  }, []);

  // Restore scroll position after selectedTagsMap changes (to prevent scroll-to-top upon dropdown selection)
  useEffect(() => {
    if (scrollPositionRef?.current !== null) {
      const modalBody = document.querySelector('.GenericModal.CourseResourceFilterModal .generic-modal-body');
      if (modalBody) {
        modalBody.scrollTop = scrollPositionRef.current;
        scrollPositionRef.current = null;
      }
    }
  }, [selectedTagsMap]);

  const handleCategoryChange = (_event, { value }, categoryId) => {
    // Preserve scroll position before state update
    const modalBody = document.querySelector('.GenericModal.CourseResourceFilterModal .generic-modal-body');
    scrollPositionRef.current = modalBody?.scrollTop || 0;

    const newMap = new Map(selectedTagsMap);
    if (value && value.length > 0) {
      newMap.set(categoryId, value);
    } else {
      newMap.delete(categoryId);
    }
    setSelectedTagsMap(newMap);
  };

  const handleClearAllSelections = () => {
    setSelectedTagsMap(new Map());
  };

  const renderClearAllSelections = () => {
    return (
      <div className='clear-all-selections-wrapper'
        onClick={handleClearAllSelections}
        onKeyUp={(event) => {
          if (event?.key === 'Enter' || event?.key === ' ') {
            handleClearAllSelections();
          }
        }}
        role='button'
        tabIndex={0}>
        {t('clearAllSelections')}
      </div>
    );
  };

  const handleCloseModal = async (_event, actionButtonData = {}) => {
    const isConfirmButton = actionButtonData?.className?.includes?.(kebabCase(buttonKeyConfirmLabel));
    // If user selected the confirm (search) button, apply filters
    if (isConfirmButton) {
      const allSelectedTagIds = [];
      selectedTagsMap.forEach((tagIds) => {
        allSelectedTagIds.push(...tagIds);
      });
      courseResourceFilterManager.setSelectedCourseResourceFilterTagIds(allSelectedTagIds);
    }

    if (onClose) {
      onClose();
    }
    dialogManager.closeDialog(DIALOG_NAMES.COURSE_RESOURCE_FILTER_MODAL);
  };

  const getTagItemsAsOptions = (tags) => {
    return tags.map((tag, index) => ({
      key: tag.id + index,
      text: tag.name,
      value: tag.id
    }));
  };

  const renderCategoryDropdowns = () => {
    return courseResourceFilterManager.availableCourseResourceFilterCategories.map((category) => {
      const value = selectedTagsMap.get(category.id) || [];

      return (
        <Container key={category.id}>
          <div className='filter-dropdown-section'>
            <div className='filter-label'>{category.name}</div>
            <Dropdown
              className='course-resource-filter-dropdown'
              clearable
              closeOnChange
              fluid
              multiple
              onChange={(event, object) => handleCategoryChange(event, object, category.id)}
              options={getTagItemsAsOptions(category.tags)}
              placeholder={t('selectTagPlaceholder')}
              search
              selection
              value={value} />
          </div>
        </Container>
      );
    });
  };

  const renderModalBody = () => {
    if (!hasCategories) {
      return (
        <div className='course-resource-filter-modal-content zero-state'>
          <p>{t('noFiltersAvailable')}</p>
        </div>
      );
    }

    return (
      <div className='course-resource-filter-modal-content'>
        <div className='course-resource-filter-modal-header'>
          {renderClearAllSelections()}
        </div>
        {renderCategoryDropdowns()}
      </div>
    );
  };

  return (
    <GenericModal
      bodyMsg={renderModalBody}
      buttonKeyCancelLabel={buttonKeyCancelLabel}
      buttonKeyConfirmLabel={hasCategories ? buttonKeyConfirmLabel : ''}
      className={className}
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      disableKeystroke={disableKeystroke}
      disableMove={disableMove}
      disableResize={disableResize}
      headerMsg={t('modalTitle')}
      initHeight={initHeight || minHeight}
      initWidth={initWidth || minWidth}
      isOpen={isOpen}
      minHeight={minHeight}
      minWidth={minWidth}
      onClose={handleCloseModal}
      onOpen={onOpen}
      onRequestClose={onRequestClose}
      overlay={overlay}
      top={top} />
  );
});

export default CourseResourceFilterModal;

SatCoreRegister('CourseResourceFilterModal', CourseResourceFilterModal);
